import { isNil, keys } from "@mixitone/util";
import { Field, InsertQuery, Query, QueryFilter, QueryFilterValue, UpdateQuery } from "..";
import { ReferenceDescriptor } from "../reference";
import { CountResponse, DataResponse, DatabaseAdapter, SingleResponse } from "./DatabaseAdapter";
import {
  chunkValues,
  findOversizedInFilter,
  getEffectiveFilter,
  withInFilterValues,
} from "./supabaseInFilterBatches";
import { SupabaseClient } from "@supabase/supabase-js";

type PostgrestFilterBuilder =
  | ReturnType<ReturnType<SupabaseClient["from"]>["select"]>
  | ReturnType<ReturnType<SupabaseClient["from"]>["update"]>;

function applyFilter<B extends PostgrestFilterBuilder>(builder: B, filter: QueryFilter<any>) {
  return keys(filter).reduce((acc, k) => {
    const column = k;
    const value = filter[k] as QueryFilterValue<any>;
    if (!value) return acc;
    if (value.op === "is_null") {
      return acc.is(column, null);
    } else if (value.op === "is_not_null") {
      return acc.not(column, "is", null);
    }
    const op = value.op;
    const fn = acc[op as unknown as keyof typeof acc] as (k: string, v: any) => any;
    if (!fn) {
      console.error("no fn for", value);
    }
    return fn.apply(acc, [column as string, "value" in value ? value.value : null]);
  }, builder);
}

class SupabaseBuilder {
  constructor(
    private supabase: SupabaseClient<any>,
    private query: Query<any, any>,
  ) {}

  get builder() {
    return this.supabase.from(this.query.tableName);
  }

  get builderForCount() {
    return this.applyFilter(this.builder.select(this.selection, { count: "exact", head: true }));
  }

  get builderWithFilter() {
    let builder = this.applyFilter(this.builder.select(this.selection));
    if (this.query.options.limit) {
      builder = builder.limit(this.query.options.limit);
    }
    return builder;
  }

  get builderWithOrder() {
    return this.applyOrder(this.builderWithFilter);
  }

  get selection() {
    const selection = [...this.query.selection];

    if (this.query.options.preloadCounts) {
      for (const collection of this.query.options.preloadCounts) {
        const collectionConfig = this.query.modelClass.config.collections[collection];
        const tableName = collectionConfig.model().tableName;

        if (tableName !== collection) {
          selection.push(`${collection}:${String(collectionConfig.model().tableName)}(count)`);
        } else {
          selection.push(`${String(collectionConfig.model().tableName)}(count)`);
        }
      }
    }

    if (this.query.preloadReferences) {
      for (const reference of this.query.preloadReferences) {
        const referenceConfig = this.query.modelClass.config.references[
          reference
        ] as ReferenceDescriptor<any>;
        selection.push(`${String(reference)}:${referenceConfig.key}(*)`);
      }
    }

    if (this.query.joinQueries) {
      for (const join of this.query.joinQueries) {
        selection.push(`${join.tableName}!inner(${join.selection})`);
      }
    }

    return selection.join(",");
  }

  private applyFilter(builder: ReturnType<ReturnType<(typeof this.supabase)["from"]>["select"]>) {
    const filter: QueryFilter<any> = {
      ...this.query.filter,
      ...this.query.joinsFilter,
    };

    if (isNil(filter["is_deleted"]) && !("includeDeleted" in this.query.options)) {
      filter["is_deleted"] = { op: "eq", value: false };
    }

    return applyFilter(builder, filter);
  }

  private applyOrder(builder: ReturnType<ReturnType<(typeof this.supabase)["from"]>["select"]>) {
    if (this.query.order) {
      const orders = Array.isArray(this.query.order) ? this.query.order : [this.query.order];

      let orderedBuilder = builder;
      orders.forEach((order, index) => {
        if (index === 0) {
          orderedBuilder = orderedBuilder.order(order.column, {
            ascending: order.direction === "asc",
          });
        } else {
          orderedBuilder = orderedBuilder.order(order.column, {
            ascending: order.direction === "asc",
          });
        }
      });

      return orderedBuilder;
    } else {
      return builder;
    }
  }
}

class SupabaseInsertBuilder {
  constructor(
    private supabase: SupabaseClient<any>,
    private query: InsertQuery<any, any>,
  ) {}

  get builder() {
    return this.supabase.from(this.query.tableName);
  }

  get selection() {
    return this.query.selection.join(",");
  }

  async insert(data: Record<string, any>) {
    return this.builder
      .insert(data)
      .select(this.selection)
      .single()
      .then((r) => r);
  }
}

class SupabaseUpdateBuilder {
  constructor(
    private supabase: SupabaseClient<any>,
    private query: UpdateQuery<any, any>,
  ) {}

  get builder() {
    return this.supabase.from(this.query.tableName);
  }

  get selection() {
    return this.query.selection.join(",");
  }

  async update(data: Record<string, any>) {
    let q = applyFilter(this.builder.update(data), this.query.filter).select(this.selection);

    if (this.query.options.single) {
      // @ts-ignore
      q = q.single();
    }

    return q.then((r) => r);
  }

  async delete() {
    if (Object.keys(this.query.filter).length === 0) {
      throw new Error("Supabase does not allow deleting all records");
    }
    if (!this.query.modelClass.hasField("is_deleted")) {
      throw new Error("Supabase does not allow deleting records without is_deleted field");
    }

    let updates: Record<string, any> = {
      is_deleted: true,
    };

    if (this.query.modelClass.hasField("updated_at")) {
      updates["updated_at"] = new Date().toISOString();
    }

    return applyFilter(this.builder.update(updates), this.query.filter).then((r) => r);
  }
}

export default class SupabaseDatabaseAdapter extends DatabaseAdapter {
  constructor(public supabase: SupabaseClient) {
    super();
  }

  convertType(_field: Field, value: any) {
    return value;
  }

  private async executeOnce<Data>(query: Query<any, any>): Promise<DataResponse<Data>> {
    const builder = new SupabaseBuilder(this.supabase, query).builderWithOrder;
    return (await networkIntercept(builder.then((r) => r))) as DataResponse<Data>;
  }

  private async countOnce(query: Query<any, any>): Promise<CountResponse> {
    const builder = new SupabaseBuilder(this.supabase, query).builderForCount;
    return (await networkIntercept(builder.then((r) => r))) as CountResponse;
  }

  override async execute<Data>(query: Query<any, any>): Promise<DataResponse<Data>> {
    const oversized = findOversizedInFilter(getEffectiveFilter(query));
    if (!oversized) {
      return this.executeOnce(query);
    }

    const responses = await Promise.all(
      chunkValues(oversized.values).map((batchValues) =>
        this.executeOnce(withInFilterValues(query, oversized.field, batchValues)),
      ),
    );

    const errorResponse = responses.find((response) => response.error);
    if (errorResponse) return errorResponse as DataResponse<Data>;

    let data = responses.flatMap((response) => response.data ?? []);
    if (query.options.limit !== undefined && data.length > query.options.limit) {
      data = data.slice(0, query.options.limit);
    }

    return { data } as DataResponse<Data>;
  }

  override async count(query: Query<any, any>): Promise<CountResponse> {
    const oversized = findOversizedInFilter(getEffectiveFilter(query));
    if (!oversized) {
      return this.countOnce(query);
    }

    const responses = await Promise.all(
      chunkValues(oversized.values).map((batchValues) =>
        this.countOnce(withInFilterValues(query, oversized.field, batchValues)),
      ),
    );

    const errorResponse = responses.find((response) => response.error);
    if (errorResponse) return errorResponse;

    return {
      count: responses.reduce((total, response) => total + (response.count ?? 0), 0),
    };
  }

  override async insert<Data>(
    query: InsertQuery<any, any>,
    data: Record<string, any>,
  ): Promise<SingleResponse<Data>> {
    let builder = new SupabaseInsertBuilder(this.supabase, query);
    const response = await networkIntercept(builder.insert(data));
    return response as SingleResponse<Data>;
  }

  override async update<Data>(
    query: UpdateQuery<any, any>,
    data: Record<string, any>,
  ): Promise<DataResponse<Data>> {
    let builder = new SupabaseUpdateBuilder(this.supabase, query);
    const response = await networkIntercept(builder.update(data));
    return response as DataResponse<Data>;
  }

  override async delete(query: UpdateQuery<any, any>): Promise<CountResponse> {
    let builder = new SupabaseUpdateBuilder(this.supabase, query);
    const response = await networkIntercept(builder.delete());
    return response;
  }
}

function networkIntercept<T>(promise: PromiseLike<T>): PromiseLike<T> {
  // return new Promise((resolve) => {
  //   setTimeout(() => {
  //     promise.then(resolve);
  //   }, 181);
  // });
  return promise;
}
